import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TimeEntry } from '../types/database';
import { getSupabaseService } from '../lib/supabase-services';

// Query keys
export const timeEntriesKeys = {
  all: ['timeEntries'] as const,
  byMonth: (year: number, month: number) => [...timeEntriesKeys.all, 'month', year, month] as const,
  byDate: (date: string) => [...timeEntriesKeys.all, 'date', date] as const,
};

// Custom hook for time entries with TanStack Query
export const useTimeEntriesQuery = (year: number, month: number, enabled = true) => {
  return useQuery({
    queryKey: timeEntriesKeys.byMonth(year, month),
    queryFn: async () => {
      const supabaseService = getSupabaseService();
      return await supabaseService.getTimeEntriesByMonth(year, month);
    },
    enabled,
  });
};

// Hook for getting a specific time entry by date
export const useTimeEntryByDateQuery = (date: string) => {
  return useQuery({
    queryKey: timeEntriesKeys.byDate(date),
    queryFn: async () => {
      const supabaseService = getSupabaseService();
      return await supabaseService.getTimeEntryByDate(date);
    },
    enabled: !!date,
  });
};

// Mutation for adding time entry
export const useAddTimeEntryMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (entry: Omit<TimeEntry, 'id' | 'created_at' | 'updated_at'>) => {
      const supabaseService = getSupabaseService();
      return await supabaseService.insertTimeEntry(entry);
    },
    onSuccess: (id, entry) => {
      // Invalidate and refetch time entries for the month
      const date = new Date(entry.date);
      queryClient.invalidateQueries({ 
        queryKey: timeEntriesKeys.byMonth(date.getFullYear(), date.getMonth() + 1) 
      });
      
      // Invalidate monthly summary to update earnings
      queryClient.invalidateQueries({ 
        queryKey: ['monthlyRates', 'summary', date.getFullYear(), date.getMonth() + 1] 
      });
      
      // Add the new entry to the cache
      queryClient.setQueryData(
        timeEntriesKeys.byDate(entry.date),
        {
          ...entry,
          id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      );
    },
  });
};

// Mutation for updating time entry
export const useUpdateTimeEntryMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, entry }: { id: number; entry: Partial<TimeEntry> }) => {
      const supabaseService = getSupabaseService();
      return await supabaseService.updateTimeEntry(id, entry);
    },
    onSuccess: (updatedEntry, { entry }) => {
      // Invalidate time entries for the month
      if (entry.date) {
        const date = new Date(entry.date);
        queryClient.invalidateQueries({ 
          queryKey: timeEntriesKeys.byMonth(date.getFullYear(), date.getMonth() + 1) 
        });
        
        // Invalidate monthly summary to update earnings
        queryClient.invalidateQueries({ 
          queryKey: ['monthlyRates', 'summary', date.getFullYear(), date.getMonth() + 1] 
        });
      }
      
      // Update the specific entry in cache
      queryClient.setQueryData(
        timeEntriesKeys.byDate(entry.date || ''),
        updatedEntry
      );
    },
  });
};

// Mutation for deleting time entry
export const useDeleteTimeEntryMutation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: number) => {
      const supabaseService = getSupabaseService();
      return await supabaseService.deleteTimeEntry(id);
    },
    onSuccess: () => {
      // Invalidate all time entries queries
      queryClient.invalidateQueries({ queryKey: timeEntriesKeys.all });
      
      // Invalidate all monthly summaries to update earnings
      queryClient.invalidateQueries({ queryKey: ['monthlyRates', 'summary'] });
    },
  });
};
